import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Send, CheckCircle, AlertCircle, Sparkles, Clock, FileText } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const { t, translations } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    country: '',
    volume: '',
    message: ''
  });
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      await axios.post(`${API}/contact`, formData);
      setStatus('success');
      setFormData({
        name: '',
        email: '',
        company: '',
        country: '',
        volume: '',
        message: ''
      });
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.response?.data?.detail || t('contact.form.error'));
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-16 lg:py-24 relative overflow-hidden" data-testid="contact-hero">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-1/4 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-[120px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 font-display" data-testid="contact-title">
            {t('contact.hero.title')}
          </h1>
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
            {t('contact.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 lg:py-24" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Form */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
              {status === 'success' ? (
                <div className="text-center py-12" data-testid="form-success">
                  <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-emerald-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {t('contact.form.success')}
                  </h3>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">
                      {t('contact.form.name')}
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder={t('contact.form.namePlaceholder')}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-colors"
                      data-testid="input-name"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">
                      {t('contact.form.email')}
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder={t('contact.form.emailPlaceholder')}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-colors"
                      data-testid="input-email"
                    />
                  </div>

                  {/* Company */}
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">
                      {t('contact.form.company')}
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder={t('contact.form.companyPlaceholder')}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-colors"
                      data-testid="input-company"
                    />
                  </div>

                  {/* Country */}
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">
                      {t('contact.form.country')}
                    </label>
                    <select
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-colors"
                      data-testid="select-country"
                    >
                      <option value="" className="bg-zinc-900">{t('contact.form.countryPlaceholder')}</option>
                      {translations.contact.countries.map((country, index) => (
                        <option key={index} value={country} className="bg-zinc-900">
                          {country}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Volume */}
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">
                      {t('contact.form.volume')}
                    </label>
                    <select
                      name="volume"
                      value={formData.volume}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-colors"
                      data-testid="select-volume"
                    >
                      <option value="" className="bg-zinc-900">{t('contact.form.volumePlaceholder')}</option>
                      {translations.contact.volumes.map((vol, index) => (
                        <option key={index} value={vol} className="bg-zinc-900">
                          {vol}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-medium text-zinc-300 mb-2">
                      {t('contact.form.message')}
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      placeholder={t('contact.form.messagePlaceholder')}
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder:text-zinc-500 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-colors resize-none"
                      data-testid="textarea-message"
                    />
                  </div>

                  {/* Error Message */}
                  {status === 'error' && (
                    <div className="flex items-center gap-2 text-red-400 text-sm" data-testid="form-error">
                      <AlertCircle className="w-4 h-4" />
                      <span>{errorMessage}</span>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={status === 'loading'}
                    className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-emerald-500 text-black font-bold rounded-full hover:bg-emerald-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-emerald-500/30"
                    data-testid="submit-button"
                  >
                    {status === 'loading' ? (
                      <>
                        <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                        {t('contact.form.submitting')}
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        {t('contact.form.submit')}
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Info Section */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-white mb-6 font-display">
                  {t('contact.info.title')}
                </h2>
                <div className="space-y-6">
                  {translations.contact.info.items.map((item, index) => (
                    <div key={index} className="flex items-start gap-4" data-testid={`info-item-${index}`}>
                      <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center shrink-0">
                        {index === 0 && <Sparkles className="w-6 h-6 text-emerald-400" />}
                        {index === 1 && <Clock className="w-6 h-6 text-emerald-400" />}
                        {index === 2 && <FileText className="w-6 h-6 text-emerald-400" />}
                      </div>
                      <div>
                        <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                        <p className="text-zinc-400 text-sm">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Compliance Badges */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="text-white font-semibold mb-4">European Compliance</h3>
                <div className="flex flex-wrap gap-3">
                  <div className="px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                    <span className="text-sm text-emerald-400 font-mono">PSD2 Ready</span>
                  </div>
                  <div className="px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <span className="text-sm text-amber-400 font-mono">GDPR Secure</span>
                  </div>
                  <div className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg">
                    <span className="text-sm text-zinc-400 font-mono">PCI DSS Level 1</span>
                  </div>
                  <div className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg">
                    <span className="text-sm text-zinc-400 font-mono">SEPA Integrated</span>
                  </div>
                </div>
              </div>

              {/* Currencies */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                <h3 className="text-white font-semibold mb-4">Supported Currencies</h3>
                <div className="flex flex-wrap gap-3">
                  <span className="px-3 py-1.5 bg-zinc-800 rounded-lg text-sm text-zinc-300">EUR</span>
                  <span className="px-3 py-1.5 bg-zinc-800 rounded-lg text-sm text-zinc-300">CZK</span>
                  <span className="px-3 py-1.5 bg-zinc-800 rounded-lg text-sm text-zinc-300">PLN</span>
                  <span className="px-3 py-1.5 bg-zinc-800 rounded-lg text-sm text-zinc-300">GBP</span>
                  <span className="px-3 py-1.5 bg-zinc-800 rounded-lg text-sm text-zinc-300">CHF</span>
                  <span className="px-3 py-1.5 bg-zinc-800 rounded-lg text-sm text-zinc-300">+30 more</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
